import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User, Session } from "@supabase/supabase-js";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isVendor: boolean;
  isModerator: boolean;
  isAdmin: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
  isVendor: false,
  isModerator: false,
  isAdmin: false,
  signOut: async () => {},
});

export const useAuth = () => useContext(AuthContext);

const ROLES_TIMEOUT_MS = 2500;

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isVendor, setIsVendor] = useState(false);
  const [isModerator, setIsModerator] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  const mountedRef = useRef(true);
  const authRequestRef = useRef(0);

  const withTimeout = async <T,>(promise: Promise<T>, timeoutMs: number): Promise<T> => {
    return await Promise.race([
      promise,
      new Promise<T>((_, reject) => {
        setTimeout(() => reject(new Error("Roles fetch timeout")), timeoutMs);
      }),
    ]);
  };

  const fetchAndSetRoles = async (userId: string, requestId: number) => {
    try {
      const { data } = await withTimeout(
        (async () => {
          return await supabase
            .from("user_roles")
            .select("role")
            .eq("user_id", userId);
        })(),
        ROLES_TIMEOUT_MS
      );

      if (!mountedRef.current || authRequestRef.current !== requestId) return;

      const roles = (data || []).map((r) => r.role);
      setIsVendor(roles.includes("vendor"));
      setIsModerator(roles.includes("moderator"));
      setIsAdmin(roles.includes("admin"));
    } catch {
      if (!mountedRef.current || authRequestRef.current !== requestId) return;
      clearRoles();
    }
  };

  const clearRoles = () => {
    setIsVendor(false);
    setIsModerator(false);
    setIsAdmin(false);
  };

  const syncAuthState = async (nextSession: Session | null, shouldBlock = false) => {
    const requestId = ++authRequestRef.current;

    // Only show loading spinner on initial load, not on token refresh
    if (shouldBlock && mountedRef.current && !user) {
      setLoading(true);
    }

    setSession(nextSession);
    setUser(nextSession?.user ?? null);

    if (!nextSession?.user) {
      clearRoles();
      if (mountedRef.current && authRequestRef.current === requestId) {
        setLoading(false);
      }
      return;
    }

    await fetchAndSetRoles(nextSession.user.id, requestId);

    if (mountedRef.current && authRequestRef.current === requestId) {
      setLoading(false);
    }
  };

  useEffect(() => {
    mountedRef.current = true;

    const initialize = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!mountedRef.current) return;
        await syncAuthState(session, true);
      } catch {
        if (!mountedRef.current) return;
        clearRoles();
        setLoading(false);
      }
    };

    void initialize();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, nextSession) => {
        void syncAuthState(nextSession, !!nextSession?.user);
      }
    );

    return () => {
      mountedRef.current = false;
      subscription.unsubscribe();
    };
  }, []);

  const signOut = async () => {
    authRequestRef.current += 1;
    setSession(null);
    setUser(null);
    clearRoles();
    setLoading(false);

    try {
      await supabase.auth.signOut();
    } catch (e) {
      console.error('Sign out error:', e);
    }
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, isVendor, isModerator, isAdmin, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};
